import React from "react";
import { Input, TextField } from "@mui/material";
import { createUseStyles } from "$utils/createUseStyles";
import dateFormat from "dateformat";
import { Text, IField, useFormContext } from "$components";
import { DEFAULT_DATE_FORMAT } from "$utils/utils";
import { theme } from "$utils/theme/bataillog";

export interface ITextInput extends IField {
  outlineLabel?: string;
  groupClass?: string;
  checkRegexOnType?: boolean;
  checkOnBlurRegex?: string;
  errorRequired: boolean;
  errorRegex: boolean;
  additionalText?: string;
  uppercase?: boolean;
  multiline?: boolean;
  rows?: number;
}

interface InputType {
  value?: string;
  errorRegex?: boolean;
  errorRequired?: boolean;
}

function TextInput({
  label,
  className,
  requiredText,
  regex,
  regexText = "",
  name,
  placeholder = "",
  Child,
  outlineLabel = "",
  groupClass,
  checkRegexOnType,
  checkOnBlurRegex,
  errorRequired = false,
  errorRegex = false,
  // value: DefValue,
  variant,
  additionalText = "",
  uppercase = false,
  multiline = false,
  rows = 5,
}: ITextInput) {
  const classes = useStyles();
  const Type: any = outlineLabel === "" ? Input : TextField;
  const textOutline: object =
    outlineLabel === "" ? {} : { variant: "outlined", label: outlineLabel };

  const context = useFormContext();
  const { values, setFormValues } = context;
  const inputText = context[name];
  const [error, setError] = React.useState<string>("");

  const setStateValue = (dataInput: InputType) => {
    const newValue = {
      ...values[name],
      ...dataInput,
    };
    setFormValues({
      [name]: newValue,
    });
  };

  const OnChange = (e: any) => {
    const val = uppercase
      ? e?.target?.value.toUpperCase() || ""
      : e?.target?.value || "";
    if (checkRegexOnType && regex) {
      if (val === "") {
        setStateValue({
          value: val,
        });
        return;
      }
      const rgx = new RegExp(regex);
      if (rgx.test(val)) {
        setStateValue({
          value: val,
          errorRegex: false,
          errorRequired: false,
        });
        return;
      }
      setError("");
      setStateValue({
        errorRegex: false,
        errorRequired: false,
      });
    } else {
      setError("");
      setStateValue({
        value: val,
        errorRegex: false,
        errorRequired: false,
      });
    }
  };

  const OnBlur = (e: any) => {
    let val = uppercase
      ? e?.target?.value.toUpperCase() || ""
      : e?.target?.value || "";
    let ok = true;
    if (val === "" && !requiredText) {
      setError("");
      setStateValue({
        errorRequired: false,
        errorRegex: false,
      });
      return;
    }
    if (val !== "" && checkOnBlurRegex) {
      if (variant && variant === "date") {
        val = val.replaceAll("-", "/");
        const dt = new Date(val).getTime();
        val = Number.isNaN(dt)
          ? "NaN"
          : dateFormat(new Date(val), DEFAULT_DATE_FORMAT);
      }
      const rgx = new RegExp(checkOnBlurRegex);
      if (!rgx.test(val)) {
        ok = false;
      }
    }
    if (requiredText && val === "") {
      setError(requiredText);
      setStateValue({
        errorRequired: true,
        errorRegex: false,
      });
    } else if (!ok) {
      setError(regexText || "");
      setStateValue({
        errorRegex: true,
        errorRequired: false,
      });
    } else {
      setError("");
      setStateValue({
        errorRequired: false,
        errorRegex: false,
      });
    }
  };

  React.useEffect(() => {
    if (errorRequired) {
      setError(requiredText || "");
    } else if (errorRegex) {
      setError(regexText || "");
    }
  }, [errorRegex, errorRequired, regexText, requiredText]);

  return (
    <div className={`${classes.inputGroup} ${groupClass ?? ""}`}>
      {label && (
        <label className={classes.inputLabel} htmlFor={name}>
          <Text>{label}</Text>
          <Text className={classes.additionalText}>{additionalText}</Text>
        </label>
      )}
      <Type
        type={variant || undefined}
        inputProps={{ lang: "fr-FR" }}
        name={name}
        onBlur={OnBlur}
        onChange={OnChange}
        value={values[name]?.value || ""}
        autoComplete="off"
        spellCheck="false"
        autoCapitalize="none"
        id={name}
        disabled={values[name]?.disabled || false}
        placeholder={placeholder}
        className={`${classes.textInput} ${className ?? ""}`}
        multiline={multiline}
        rows={multiline ? rows : undefined}
        style={{ paddingLeft: 8, paddingRight: 8 }}
        {...textOutline}
      />
      <Text className={classes.errorField}>{error !== "" && error}</Text>
      {inputText?.Child || Child || null}
    </div>
  );
}
export default React.memo(TextInput);

const useStyles = createUseStyles(() => ({
  inputGroup: {
    display: "flex",
    flexDirection: "column",
    marginBottom: "14px",
    flex: 1,
    marginRight: "10px",
    "&.ProjectCode": {
      flex: "unset",
      width: "288px",
    },
  },
  inputLabel: {
    marginBottom: "16px",
  },
  textInput: {
    flex: 1,
    border: `1px solid ${theme.color.gray2}`,
    paddingLeft: 10,
    paddingRight: 10,
    "&:before": {
      display: "none",
    },
    "& input": {
      height: 29,
      fontSize: 15,
    },
    "&.Mui-disabled": {
      backgroundColor: theme.color.gray2,
      color: "#000 !important",
    },
  },
  errorField: {
    minHeight: 20,
    color: theme.color.red,
    fontSize: 12,
  },
  additionalText: {
    fontStyle: "italic",
  },
}));
